import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';
import { useB2BUser } from './B2BUserContext';
import { B2BOrderPrintView, type B2BOrderPrintData } from './B2BOrderPrintView';
import {
  Loader as Loader2,
  CircleAlert as AlertCircle,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  Store,
  Package,
  ShoppingBag,
  BookOpen,
  RefreshCw,
  CircleArrowUp as ArrowUpCircle,
  CircleArrowDown as ArrowDownCircle,
  CircleCheck as CheckCircle2,
  Circle as XCircle,
  Clock,
  Truck,
  RotateCcw,
  FileText,
  Search,
  X,
  ChevronsDown,
  SlidersHorizontal,
  Mail,
} from 'lucide-react';
import SendDocumentEmailModal from '@/components/modals/SendDocumentEmailModal';
import { computeOrderDiscounts, type DiscountType } from '@/lib/b2bDiscounts';

// ─── Constants ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderStatus = 'pending' | 'processing' | 'locked' | 'cancelled';

interface OrderLine {
  id: string;
  requested_quantity: number;
  approved_quantity: number;
  unit_price: number;
  total_line_price: number;
  discount: number;
  net_line_price: number;
  product_name: string;
  product_sku: string;
  product_image_url: string | null;
}

interface Order {
  id: string;
  status: OrderStatus;
  total_amount: number;
  created_at: string;
  updated_at: string;
  supplier_name: string | null;
  discount_total: number;
  discount_reason: string | null;
  lines: OrderLine[];
}

interface Filters {
  dateFrom: string;
  dateTo: string;
  amountMin: string;
  amountMax: string;
  status: OrderStatus | '';
}

const EMPTY_FILTERS: Filters = {
  dateFrom: '',
  dateTo: '',
  amountMin: '',
  amountMax: '',
  status: '',
};

// ─── RMA Types ──────────────────────────────────────────────────────────────

type RMAStatus = 'pending' | 'approved' | 'partial' | 'rejected';

interface RMALine {
  id: string;
  product_name: string;
  product_sku: string;
  requested_qty: number;
  received_qty: number | null;
  unit_price: number;
  return_reason: string | null;
}

interface RMARequest {
  id: string;
  status: RMAStatus;
  return_reason: string | null;
  admin_notes: string | null;
  total_lines: number;
  created_at: string;
  reviewed_at: string | null;
  lines: RMALine[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(n: number) {
  return `₪${n.toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('he-IL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateShort(iso: string) {
  return new Date(iso).toLocaleDateString('he-IL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function filtersAreActive(f: Filters) {
  return f.dateFrom || f.dateTo || f.amountMin || f.amountMax || f.status;
}

// ─── Status badge ────────────────────────────────────────────────────────────

const STATUS_META: Record<
  OrderStatus,
  { label: string; icon: React.ReactNode; pill: string }
> = {
  pending: {
    label: 'ממתינה לאישור',
    icon: <Clock className="w-3.5 h-3.5" />,
    pill: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  processing: {
    label: 'בטיפול',
    icon: <Truck className="w-3.5 h-3.5" />,
    pill: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  locked: {
    label: 'אושרה סופית',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    pill: 'bg-green-100 text-green-700 border-green-200',
  },
  cancelled: {
    label: 'בוטלה',
    icon: <XCircle className="w-3.5 h-3.5" />,
    pill: 'bg-gray-100 text-gray-500 border-gray-200',
  },
};

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: 'pending', label: 'ממתינה לאישור' },
  { value: 'processing', label: 'בטיפול' },
  { value: 'locked', label: 'אושרה סופית' },
  { value: 'cancelled', label: 'בוטלה' },
];

function StatusBadge({ status }: { status: OrderStatus }) {
  const m = STATUS_META[status] ?? STATUS_META.pending;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                  text-[11px] font-semibold border ${m.pill} leading-none`}
    >
      {m.icon}
      {m.label}
    </span>
  );
}

// ─── RMA status badge ───────────────────────────────────────────────────────

const RMA_STATUS_META: Record<
  RMAStatus,
  { label: string; icon: React.ReactNode; pill: string }
> = {
  pending: {
    label: 'ממתין לטיפול',
    icon: <Clock className="w-3.5 h-3.5" />,
    pill: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  approved: {
    label: 'אושר',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    pill: 'bg-green-100 text-green-700 border-green-200',
  },
  partial: {
    label: 'אושר חלקית',
    icon: <Package className="w-3.5 h-3.5" />,
    pill: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  rejected: {
    label: 'נדחה',
    icon: <XCircle className="w-3.5 h-3.5" />,
    pill: 'bg-red-100 text-red-600 border-red-200',
  },
};

function RMAStatusBadge({ status }: { status: RMAStatus }) {
  const m = RMA_STATUS_META[status] ?? RMA_STATUS_META.pending;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                  text-[11px] font-semibold border ${m.pill} leading-none`}
    >
      {m.icon}
      {m.label}
    </span>
  );
}

// ─── Quantity delta display ──────────────────────────────────────────────────

function QtyDisplay({
  requested,
  approved,
}: {
  requested: number;
  approved: number;
}) {
  if (requested === approved) {
    return (
      <span className="text-sm font-bold text-gray-800 tabular-nums">
        {approved.toLocaleString('he-IL')}
      </span>
    );
  }

  const reduced = approved < requested;

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span className="text-xs text-gray-400 line-through tabular-nums">
        {requested.toLocaleString('he-IL')}
      </span>
      <span
        className={`inline-flex items-center gap-0.5 text-sm font-bold tabular-nums
                    ${reduced ? 'text-amber-600' : 'text-green-600'}`}
      >
        {reduced ? (
          <ArrowDownCircle className="w-3.5 h-3.5 shrink-0" />
        ) : (
          <ArrowUpCircle className="w-3.5 h-3.5 shrink-0" />
        )}
        {approved.toLocaleString('he-IL')}
      </span>
      <span
        className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5
                    rounded-full border leading-none
                    ${
                      reduced
                        ? 'bg-amber-50 text-amber-600 border-amber-200'
                        : 'bg-green-50 text-green-600 border-green-200'
                    }`}
      >
        {reduced ? 'שונה' : 'עודכן'}
      </span>
    </span>
  );
}

// ─── Order line row ──────────────────────────────────────────────────────────

function OrderLineRow({ line }: { line: OrderLine }) {
  const modified = line.approved_quantity !== line.requested_quantity;

  return (
    <div
      className={`flex items-start gap-3 py-3 px-3 rounded-xl
                  ${
                    modified
                      ? 'bg-amber-50/60 border border-amber-100'
                      : 'bg-gray-50/60'
                  }`}
    >
      <div className="w-11 h-11 shrink-0 rounded-lg overflow-hidden bg-gray-100">
        {line.product_image_url ? (
          <img
            src={line.product_image_url}
            alt={line.product_name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-gray-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
          {line.product_name}
        </p>
        <p className="text-[10px] text-gray-400 font-mono" dir="ltr">
          {line.product_sku}
        </p>
      </div>
      <div className="shrink-0 text-left space-y-1 min-w-[80px]">
        <div className="flex justify-end">
          <QtyDisplay
            requested={line.requested_quantity}
            approved={line.approved_quantity}
          />
        </div>
        <p className="text-[10px] text-gray-400 text-left">
          × {formatPrice(line.unit_price)}
        </p>
        {line.discount > 0 && (
          <p className="text-[10px] text-gray-400 text-left line-through">
            {formatPrice(line.total_line_price)}
          </p>
        )}
        <p className={`text-sm font-bold text-left ${line.discount > 0 ? 'text-emerald-700' : 'text-gray-800'}`}>
          {formatPrice(line.net_line_price)}
        </p>
        {line.discount > 0 && (
          <p className="text-[10px] font-medium text-emerald-700 text-left">
            הנחה {formatPrice(line.discount)}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Order card ──────────────────────────────────────────────────────────────

function OrderCard({
  order,
  isAgent,
  onInstantApprove,
  vatRate,
  isVatLiable,
  returnsEnabled,
  onViewDocument,
  clientId,
  companyId,
}: {
  order: Order;
  isAgent: boolean;
  onInstantApprove: (orderId: string) => Promise<void>;
  vatRate: number | null;
  isVatLiable: boolean;
  returnsEnabled: boolean;
  onViewDocument: (order: Order) => void;
  clientId: string | null;
  companyId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveErr, setApproveErr] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const canInstantApprove =
    isAgent && (order.status === 'pending' || order.status === 'processing');
  const canRequestReturn = order.status === 'locked' && returnsEnabled;

  async function handleInstantApprove(e: React.MouseEvent) {
    e.stopPropagation();
    setApproveErr(null);
    setApproving(true);
    try {
      await onInstantApprove(order.id);
    } catch (err) {
      setApproveErr(err instanceof Error ? err.message : 'שגיאה');
    } finally {
      setApproving(false);
    }
  }

  const hasModifiedLines = order.lines.some(
    (l) => l.approved_quantity !== l.requested_quantity,
  );

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden
                  transition-shadow hover:shadow-md
                  ${order.status === 'cancelled' ? 'opacity-70' : ''}
                  ${hasModifiedLines ? 'border-amber-200' : 'border-gray-100'}`}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-right px-4 py-3.5 flex items-start gap-3
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        aria-expanded={expanded}
      >
        <div className="shrink-0 mt-0.5 text-gray-400">
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center flex-wrap gap-2">
            <StatusBadge status={order.status} />
            {hasModifiedLines && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold
                               text-amber-600 bg-amber-50 border border-amber-200
                               px-2 py-0.5 rounded-full leading-none"
              >
                <AlertCircle className="w-3 h-3" />
                כמויות שונו
              </span>
            )}
          </div>
          {order.supplier_name && (
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
              <Store className="w-3 h-3 shrink-0 text-gray-400" />
              <span className="truncate">{order.supplier_name}</span>
            </div>
          )}
          <div className="flex items-center gap-3 text-[11px] text-gray-400">
            <span>{formatDateShort(order.created_at)}</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <Package className="w-3 h-3" />
              {order.lines.length} פריטים
            </span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-base font-bold text-gray-900">
            {formatPrice(Number(order.total_amount))}
          </p>
          {isVatLiable && vatRate != null && vatRate > 0 ? (
            <p className="text-[10px] text-gray-400 mt-0.5">
              כולל מע״מ:{' '}
              {formatPrice(
                Number(order.total_amount) * (1 + vatRate / 100),
              )}
            </p>
          ) : (
            <p className="text-[10px] text-gray-400 mt-0.5">פטור ממע״מ</p>
          )}
        </div>
      </button>

      {/* Instant-approve (agents only) */}
      {canInstantApprove && (
        <div className="px-4 pb-3" onClick={(e) => e.stopPropagation()}>
          {approveErr && (
            <p className="text-xs text-red-600 mb-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3 shrink-0" />
              {approveErr}
            </p>
          )}
          <button
            type="button"
            disabled={approving}
            onClick={handleInstantApprove}
            className="w-full flex items-center justify-center gap-2
                       py-2.5 rounded-xl font-semibold text-sm
                       bg-orange-500 hover:bg-orange-600 active:bg-orange-700
                       text-white transition-colors disabled:opacity-60
                       active:scale-[.98]"
          >
            {approving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Truck className="w-4 h-4" />
            )}
            {approving ? 'מאשר…' : 'אשר כאספקה מיידית — הפק תעודת משלוח'}
          </button>
        </div>
      )}

      {/* Expanded: order lines */}
      {expanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-gray-100 pt-3">
          <div
            className="flex items-center justify-between text-[10px]
                          font-semibold text-gray-400 uppercase tracking-wide
                          px-3 pb-1"
          >
            <span>מוצר</span>
            <span>כמות / סה״כ</span>
          </div>

          {order.lines.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">
              אין פריטים בהזמנה זו
            </p>
          ) : (
            order.lines.map((line) => (
              <OrderLineRow key={line.id} line={line} />
            ))
          )}

          {/* Footer total */}
          {order.discount_total > 0 && (
            <div className="flex items-start justify-between gap-3 px-3 py-2 rounded-xl
                            bg-emerald-50 border border-emerald-100 text-xs text-emerald-800">
              <span>
                {order.discount_reason ? `סיבת הנחה: ${order.discount_reason}` : 'הנחה על ההזמנה'}
              </span>
              <span className="font-bold tabular-nums shrink-0">-{formatPrice(order.discount_total)}</span>
            </div>
          )}
          <div
            className="flex items-center justify-between pt-2
                          border-t border-gray-100 mt-1"
          >
            <div className="space-y-0.5">
              <p className="text-[10px] text-gray-400">
                נוצרה: {formatDate(order.created_at)}
              </p>
              {order.updated_at !== order.created_at && (
                <p className="text-[10px] text-gray-400">
                  עודכנה: {formatDate(order.updated_at)}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-500 font-medium">
                סה״כ הזמנה
              </p>
              <p className="text-base font-bold text-primary-700">
                {formatPrice(Number(order.total_amount))}
              </p>
              {isVatLiable && vatRate != null && vatRate > 0 && (
                <p className="text-[11px] font-semibold text-primary-600">
                  כולל מע״מ:{' '}
                  {formatPrice(
                    Number(order.total_amount) * (1 + vatRate / 100),
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Action buttons row */}
          <div className="flex flex-col gap-2 pt-1">
            {/* View document / print */}
            <button
              type="button"
              onClick={() => onViewDocument(order)}
              className="w-full flex items-center justify-center gap-2
                         py-2.5 rounded-xl font-semibold text-sm
                         bg-blue-50 hover:bg-blue-100 active:bg-blue-200
                         text-blue-700 border border-blue-200
                         transition-colors active:scale-[.98]"
            >
              <FileText className="w-4 h-4" />
              צפה במסמך / הדפס
            </button>

            {/* Send email */}
            <button
              type="button"
              onClick={() => setShowEmailModal(true)}
              className="w-full flex items-center justify-center gap-2
                         py-2.5 rounded-xl font-semibold text-sm
                         bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-200
                         text-emerald-700 border border-emerald-200
                         transition-colors active:scale-[.98]"
            >
              <Mail className="w-4 h-4" />
              שלח במייל
            </button>

            {/* RMA */}
            {canRequestReturn && (
              <Link
                to={`/b2b/orders/${order.id}/rma`}
                className="w-full flex items-center justify-center gap-2
                           py-2.5 rounded-xl font-semibold text-sm
                           bg-orange-50 hover:bg-orange-100 active:bg-orange-200
                           text-orange-700 border border-orange-200
                           transition-colors active:scale-[.98]"
              >
                <RotateCcw className="w-4 h-4" />
                בקש החזרה (RMA)
              </Link>
            )}
          </div>
        </div>
      )}

      {showEmailModal && (
        <SendDocumentEmailModal
          onClose={() => setShowEmailModal(false)}
          clientId={clientId}
          companyId={companyId}
          b2bOrderId={order.id}
          docLabel={`תעודת הזמנה ${order.id.slice(0, 8).toUpperCase()}`}
        />
      )}
    </div>
  );
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

function FilterBar({
  filters,
  onChange,
  onClear,
  onApply,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  onClear: () => void;
  onApply: () => void;
}) {
  const set = (key: keyof Filters, value: string) =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3 shadow-sm">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <SlidersHorizontal className="w-4 h-4 text-gray-400" />
        סינון וחיפוש
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Date from */}
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1">
            מתאריך
          </label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => set('dateFrom', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary-300
                       focus:border-primary-400 bg-gray-50"
          />
        </div>
        {/* Date to */}
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1">
            עד תאריך
          </label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => set('dateTo', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary-300
                       focus:border-primary-400 bg-gray-50"
          />
        </div>
        {/* Amount min */}
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1">
            סכום מינימלי (₪)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={filters.amountMin}
            onChange={(e) => set('amountMin', e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary-300
                       focus:border-primary-400 bg-gray-50"
          />
        </div>
        {/* Amount max */}
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1">
            סכום מקסימלי (₪)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={filters.amountMax}
            onChange={(e) => set('amountMax', e.target.value)}
            placeholder="ללא הגבלה"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary-300
                       focus:border-primary-400 bg-gray-50"
          />
        </div>
      </div>

      {/* Status select */}
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 mb-1">
          סטטוס
        </label>
        <select
          value={filters.status}
          onChange={(e) => set('status', e.target.value as OrderStatus | '')}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl
                     focus:outline-none focus:ring-2 focus:ring-primary-300
                     focus:border-primary-400 bg-gray-50"
        >
          <option value="">הכל</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onApply}
          className="flex-1 flex items-center justify-center gap-2 py-2.5
                     rounded-xl font-semibold text-sm bg-primary-600
                     hover:bg-primary-700 active:bg-primary-800
                     text-white transition-colors active:scale-[.98]"
        >
          <Search className="w-4 h-4" />
          חפש
        </button>
        {filtersAreActive(filters) && (
          <button
            type="button"
            onClick={onClear}
            className="flex items-center justify-center gap-1.5 px-4 py-2.5
                       rounded-xl font-semibold text-sm text-gray-600
                       bg-gray-100 hover:bg-gray-200 active:bg-gray-300
                       transition-colors active:scale-[.98]"
          >
            <X className="w-3.5 h-3.5" />
            נקה
          </button>
        )}
      </div>
    </div>
  );
}

// ─── RMA Card ───────────────────────────────────────────────────────────────

function RMACard({ rma }: { rma: RMARequest }) {
  const [expanded, setExpanded] = useState(false);

  const totalAmount = rma.lines.reduce(
    (sum, l) => sum + (l.received_qty ?? l.requested_qty) * l.unit_price,
    0,
  );

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden
                  transition-shadow hover:shadow-md
                  ${rma.status === 'rejected' ? 'opacity-70 border-red-100' : 'border-orange-100'}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-right px-4 py-3.5 flex items-start gap-3
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
        aria-expanded={expanded}
      >
        <div className="shrink-0 mt-0.5 text-gray-400">
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center flex-wrap gap-2">
            <RMAStatusBadge status={rma.status} />
          </div>
          <div className="flex items-center gap-3 text-[11px] text-gray-400">
            <span>{formatDateShort(rma.created_at)}</span>
            <span>&middot;</span>
            <span className="flex items-center gap-1">
              <Package className="w-3 h-3" />
              {rma.lines.length} פריטים
            </span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-base font-bold text-gray-900">
            {formatPrice(totalAmount)}
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5 font-mono" dir="ltr">
            #{rma.id.slice(0, 8).toUpperCase()}
          </p>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-2.5 border-t border-orange-100 pt-3">
          {rma.return_reason && (
            <div className="flex items-start gap-2 px-3 py-2 bg-orange-50/60 rounded-xl text-xs text-orange-700">
              <RotateCcw className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p><span className="font-semibold">סיבה: </span>{rma.return_reason}</p>
            </div>
          )}

          {rma.admin_notes && (
            <div className="flex items-start gap-2 px-3 py-2 bg-blue-50/60 rounded-xl text-xs text-blue-700">
              <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p><span className="font-semibold">הערת צוות: </span>{rma.admin_notes}</p>
            </div>
          )}

          <div
            className="flex items-center justify-between text-[10px]
                          font-semibold text-gray-400 uppercase tracking-wide
                          px-3 pb-1"
          >
            <span>מוצר</span>
            <span>כמות / סה״כ</span>
          </div>

          {rma.lines.map((line) => {
            const qtyChanged =
              line.received_qty != null && line.received_qty !== line.requested_qty;
            return (
              <div
                key={line.id}
                className={`flex items-start gap-3 py-3 px-3 rounded-xl
                            ${qtyChanged ? 'bg-blue-50/60 border border-blue-100' : 'bg-gray-50/60'}`}
              >
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
                    {line.product_name}
                  </p>
                  <p className="text-[10px] text-gray-400 font-mono" dir="ltr">
                    {line.product_sku}
                  </p>
                  {line.return_reason && (
                    <p className="text-[10px] text-orange-500">
                      {line.return_reason}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-left space-y-1 min-w-[80px]">
                  <div className="flex justify-end">
                    {qtyChanged ? (
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-gray-400 line-through tabular-nums">
                          {line.requested_qty.toLocaleString('he-IL')}
                        </span>
                        <span className="inline-flex items-center gap-0.5 text-sm font-bold tabular-nums text-blue-600">
                          <ArrowDownCircle className="w-3.5 h-3.5 shrink-0" />
                          {(line.received_qty ?? 0).toLocaleString('he-IL')}
                        </span>
                      </span>
                    ) : (
                      <span className="text-sm font-bold text-gray-800 tabular-nums">
                        {line.requested_qty.toLocaleString('he-IL')}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 text-left">
                    &times; {formatPrice(line.unit_price)}
                  </p>
                  <p className="text-sm font-bold text-gray-800 text-left">
                    {formatPrice(
                      (line.received_qty ?? line.requested_qty) * line.unit_price,
                    )}
                  </p>
                </div>
              </div>
            );
          })}

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-1">
            <div className="space-y-0.5">
              <p className="text-[10px] text-gray-400">
                נוצרה: {formatDate(rma.created_at)}
              </p>
              {rma.reviewed_at && (
                <p className="text-[10px] text-gray-400">
                  טופלה: {formatDate(rma.reviewed_at)}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-500 font-medium">סה״כ החזרה</p>
              <p className="text-base font-bold text-orange-600">
                {formatPrice(totalAmount)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Print modal (full-screen overlay) ───────────────────────────────────────

function PrintModal({
  data,
  onClose,
  orderId,
  clientId,
  companyId,
}: {
  data: B2BOrderPrintData;
  onClose: () => void;
  orderId: string;
  clientId: string | null;
  companyId: string;
}) {
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printRef });
  const [showEmailModal, setShowEmailModal] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-100/95 backdrop-blur-sm">
      {/* Toolbar */}
      <div
        className="flex items-center justify-between px-4 py-3
                      bg-white border-b border-gray-200 shadow-sm"
        dir="rtl"
      >
        <h2 className="text-sm font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-4 h-4 text-primary-600" />
          תצוגת מסמך
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowEmailModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl
                       font-semibold text-sm bg-emerald-600 hover:bg-emerald-700
                       text-white transition-colors active:scale-[.98]"
          >
            <Mail className="w-4 h-4" />
            שלח במייל
          </button>
          <button
            type="button"
            onClick={() => handlePrint()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl
                       font-semibold text-sm bg-primary-600 hover:bg-primary-700
                       text-white transition-colors active:scale-[.98]"
          >
            <FileText className="w-4 h-4" />
            הדפס / שמור PDF
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-9 h-9 rounded-xl
                       bg-gray-100 hover:bg-gray-200 text-gray-600
                       transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Preview area */}
      <div className="flex-1 overflow-auto p-4 flex justify-center">
        <div className="transform origin-top scale-[0.7] sm:scale-[0.85] md:scale-100">
          <B2BOrderPrintView ref={printRef} data={data} />
        </div>
      </div>

      {showEmailModal && (
        <SendDocumentEmailModal
          onClose={() => setShowEmailModal(false)}
          clientId={clientId}
          companyId={companyId}
          b2bOrderId={orderId}
          docLabel={`תעודת הזמנה ${data.orderId}`}
        />
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function B2BOrdersPage() {
  const { user } = useAuth();
  const { session } = useB2BUser();
  const location = useLocation();
  const navigate = useNavigate();

  const isAgent = session?.role === 'agent';

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [approveToast, setApproveToast] = useState<{
    kind: 'success' | 'error';
    msg: string;
  } | null>(null);

  const [vatRate, setVatRate] = useState<number | null>(null);
  const [returnsEnabled, setReturnsEnabled] = useState(true);

  // RMA returns state
  const [rmaRequests, setRmaRequests] = useState<RMARequest[]>([]);
  const [rmaLoading, setRmaLoading] = useState(false);
  const [rmaExpanded, setRmaExpanded] = useState(true);
  const ordersIsVatLiable =
    session?.activeClientRecord?.is_vat_liable ?? true;

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);

  // Print modal state
  const [printData, setPrintData] = useState<B2BOrderPrintData | null>(null);
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);

  useEffect(() => {
    const companyId = session?.activeCompanyId;
    if (!companyId) return;

    supabase
      .rpc('get_company_vat_rate', { p_company_id: companyId })
      .then(({ data }) => {
        if (data != null) setVatRate(Number(data));
      });

    supabase
      .from('companies')
      .select('b2b_returns_enabled')
      .eq('id', companyId)
      .single()
      .then(({ data }) => {
        if (data)
          setReturnsEnabled(
            Boolean(
              (data as { b2b_returns_enabled: boolean }).b2b_returns_enabled,
            ),
          );
        else setReturnsEnabled(true);
      });
  }, [session?.activeCompanyId]);

  const navState = location.state as
    | { allowedCompanyIds?: string[]; companyId?: string }
    | null
    | undefined;

  const activeClientId = session?.activeClientId ?? null;
  const activeClientName = session?.activeClientRecord?.name ?? null;

  const fallbackAttempted = useRef(false);
  const clientIdsRef = useRef<string[]>([]);

  const resolveClientIds = useCallback(async (): Promise<string[]> => {
    if (!user) return [];
    if (activeClientId) return [activeClientId];
    if (session?.clientRecords && session.clientRecords.length > 0) {
      return session.clientRecords.map((r) => r.id);
    }
    const { data } = await supabase
      .from('inventory_clients')
      .select('id')
      .or(
        [
          user.email ? `email.eq.${user.email}` : null,
          user.phone ? `phone.eq.${user.phone}` : null,
        ]
          .filter(Boolean)
          .join(','),
      );
    return (data ?? []).map((r: { id: string }) => r.id);
  }, [user, session, activeClientId]);

  // ── Build & execute paginated query ──────────────────────────────────────
  const fetchOrders = useCallback(
    async (
      clientIds: string[],
      offset: number,
      activeFilters: Filters,
      append: boolean,
    ) => {
      if (!user) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        let query = supabase
          .from('b2b_orders')
          .select(
            `
            id,
            status,
            total_amount,
            created_at,
            updated_at,
            companies (
              legal_name
            ),
            order_discount_type,
            order_discount_value,
            discount_reason,
            b2b_order_lines (
              id,
              requested_quantity,
              approved_quantity,
              unit_price,
              total_line_price,
              line_discount_type,
              line_discount_value,
              inventory_products (
                name,
                sku,
                image_url
              )
            )
          `,
            { count: 'exact' },
          )
          .order('created_at', { ascending: false })
          .range(offset, offset + PAGE_SIZE - 1);

        if (clientIds.length > 0) {
          query = query.in('client_id', clientIds);
        }

        // Apply filters
        if (activeFilters.dateFrom) {
          query = query.gte('created_at', activeFilters.dateFrom + 'T00:00:00');
        }
        if (activeFilters.dateTo) {
          query = query.lte('created_at', activeFilters.dateTo + 'T23:59:59');
        }
        if (activeFilters.amountMin) {
          query = query.gte('total_amount', Number(activeFilters.amountMin));
        }
        if (activeFilters.amountMax) {
          query = query.lte('total_amount', Number(activeFilters.amountMax));
        }
        if (activeFilters.status) {
          query = query.eq('status', activeFilters.status);
        }

        const { data, error: dbErr, count } = await query;
        if (dbErr) throw dbErr;

        const mapped: Order[] = (data ?? []).map((o: any) => {
          const rawLines = (o.b2b_order_lines ?? []) as any[];
          const disc = computeOrderDiscounts(
            rawLines.map((l: any) => ({
              id: l.id,
              quantity: Number(l.approved_quantity ?? 0),
              unitPrice: Number(l.unit_price ?? 0),
              discountType: (l.line_discount_type === 'amount' ? 'amount' : 'pct') as DiscountType,
              discountValue: Number(l.line_discount_value ?? 0),
            })),
            o.order_discount_type === 'amount' ? 'amount' : 'pct',
            Number(o.order_discount_value ?? 0),
          );
          return {
          id: o.id,
          status: o.status as OrderStatus,
          total_amount: Number(o.total_amount ?? 0),
          created_at: o.created_at,
          updated_at: o.updated_at,
          supplier_name: (o.companies as any)?.legal_name ?? null,
          discount_total: disc.totalDiscount,
          discount_reason: o.discount_reason ?? null,
          lines: rawLines.map((l: any) => {
            const ld = disc.lines[l.id];
            const total = Number(l.total_line_price ?? 0);
            const discount = ld ? Math.round((ld.lineDiscount + ld.orderDiscount) * 100) / 100 : 0;
            return {
            id: l.id,
            requested_quantity: l.requested_quantity,
            approved_quantity: l.approved_quantity,
            unit_price: Number(l.unit_price ?? 0),
            total_line_price: total,
            discount,
            net_line_price: Math.round((total - discount) * 100) / 100,
            product_name:
              (l.inventory_products as any)?.name ?? 'מוצר לא זמין',
            product_sku: (l.inventory_products as any)?.sku ?? '',
            product_image_url:
              (l.inventory_products as any)?.image_url ?? null,
            };
          }),
          };
        });

        if (append) {
          setOrders((prev) => [...prev, ...mapped]);
        } else {
          setOrders(mapped);
        }

        if (count != null) {
          setTotalCount(count);
          setHasMore(offset + PAGE_SIZE < count);
        } else {
          setHasMore(mapped.length === PAGE_SIZE);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'שגיאה בטעינת ההזמנות');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [user],
  );

  // ── Fetch RMA requests ──────────────────────────────────────────────────
  const fetchRmaRequests = useCallback(
    async (clientIds: string[]) => {
      if (!user || clientIds.length === 0) return;
      setRmaLoading(true);
      try {
        const { data, error: dbErr } = await supabase
          .from('rma_requests')
          .select(`
            id,
            status,
            return_reason,
            admin_notes,
            total_lines,
            created_at,
            reviewed_at,
            rma_lines (
              id,
              requested_qty,
              received_qty,
              unit_price,
              return_reason,
              inventory_products (
                name,
                sku
              )
            )
          `)
          .in('client_id', clientIds)
          .order('created_at', { ascending: false })
          .limit(20);

        if (dbErr) throw dbErr;

        const mapped: RMARequest[] = (data ?? []).map((r: any) => ({
          id: r.id,
          status: r.status as RMAStatus,
          return_reason: r.return_reason,
          admin_notes: r.admin_notes,
          total_lines: r.total_lines ?? 0,
          created_at: r.created_at,
          reviewed_at: r.reviewed_at,
          lines: ((r.rma_lines ?? []) as any[]).map((l: any) => ({
            id: l.id,
            product_name: (l.inventory_products as any)?.name ?? 'מוצר לא זמין',
            product_sku: (l.inventory_products as any)?.sku ?? '',
            requested_qty: l.requested_qty,
            received_qty: l.received_qty,
            unit_price: Number(l.unit_price ?? 0),
            return_reason: l.return_reason,
          })),
        }));

        setRmaRequests(mapped);
      } catch {
        // silently fail — RMA section is supplementary
      } finally {
        setRmaLoading(false);
      }
    },
    [user],
  );

  // ── Initial load (re-runs when active client changes) ──────────────────
  useEffect(() => {
    if (!user) return;

    async function run() {
      fallbackAttempted.current = true;
      clientIdsRef.current = await resolveClientIds();
      setFilters(EMPTY_FILTERS);
      setAppliedFilters(EMPTY_FILTERS);
      await fetchOrders(clientIdsRef.current, 0, EMPTY_FILTERS, false);
      fetchRmaRequests(clientIdsRef.current);
    }

    run();
  }, [user, activeClientId, resolveClientIds, fetchOrders, fetchRmaRequests]);

  // ── Load more ────────────────────────────────────────────────────────────
  const handleLoadMore = () => {
    fetchOrders(
      clientIdsRef.current,
      orders.length,
      appliedFilters,
      true,
    );
  };

  // ── Apply filters ────────────────────────────────────────────────────────
  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    fetchOrders(clientIdsRef.current, 0, filters, false);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    fetchOrders(clientIdsRef.current, 0, EMPTY_FILTERS, false);
  };

  // ── Instant approve ──────────────────────────────────────────────────────
  const handleInstantApprove = useCallback(async (orderId: string) => {
    const { error: rpcErr } = await supabase.rpc('b2b_lock_order', {
      p_order_id: orderId,
    });
    if (rpcErr) throw new Error(`שגיאה: ${rpcErr.message}`);
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId
          ? { ...o, status: 'locked' as OrderStatus }
          : o,
      ),
    );
    setApproveToast({
      kind: 'success',
      msg: 'ההזמנה אושרה — תעודת משלוח הופקה!',
    });
    setTimeout(() => setApproveToast(null), 4500);
  }, []);

  // ── View document (open print modal) ─────────────────────────────────────
  const handleViewDocument = useCallback(
    (order: Order) => {
      const subtotal = Number(order.total_amount);
      const vat =
        ordersIsVatLiable && vatRate != null && vatRate > 0 ? vatRate : 0;
      const grandTotal = vat > 0 ? subtotal * (1 + vat / 100) : subtotal;

      const data: B2BOrderPrintData = {
        orderId: order.id.slice(0, 8).toUpperCase(),
        supplierName: order.supplier_name ?? '',
        clientName: activeClientName ?? '',
        date: order.created_at,
        status:
          STATUS_META[order.status]?.label ?? order.status,
        lines: order.lines.map((l) => ({
          product_name: l.product_name,
          product_sku: l.product_sku,
          requested_quantity: l.requested_quantity,
          approved_quantity: l.approved_quantity,
          unit_price: l.unit_price,
          total_line_price: l.net_line_price,
          discount: l.discount,
        })),
        subtotal,
        discountTotal: order.discount_total,
        discountReason: order.discount_reason,
        vatRate: vat > 0 ? vat : null,
        isVatLiable: ordersIsVatLiable,
        grandTotal,
      };
      setPrintData(data);
      setViewingOrderId(order.id);
    },
    [vatRate, ordersIsVatLiable],
  );

  // ── Retry ────────────────────────────────────────────────────────────────
  const handleRetry = () => {
    fallbackAttempted.current = false;
    setError(null);
    setOrders([]);
    setLoading(true);
    resolveClientIds().then((ids) => {
      clientIdsRef.current = ids;
      fetchOrders(ids, 0, appliedFilters, false);
    });
  };

  // ─── RENDER ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-4" dir="rtl">
      {/* Approve toast */}
      {approveToast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2.5 px-4 py-3
                       rounded-2xl shadow-lg text-sm font-semibold text-white
                       ${approveToast.kind === 'success' ? 'bg-green-600' : 'bg-red-600'}`}
        >
          {approveToast.kind === 'success' ? (
            <CheckCircle2 className="w-4 h-4 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0" />
          )}
          {approveToast.msg}
        </div>
      )}

      {/* Print modal */}
      {printData && (
        <PrintModal
          data={printData}
          onClose={() => { setPrintData(null); setViewingOrderId(null); }}
          orderId={viewingOrderId!}
          clientId={activeClientId}
          companyId={session?.activeCompanyId ?? ''}
        />
      )}

      {/* Page title */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary-600" />
            ההזמנות שלי
          </h1>
          {isAgent && activeClientName && (
            <p className="text-xs text-gray-500 flex items-center gap-1.5 pr-7">
              <Store className="w-3 h-3 shrink-0" />
              {activeClientName}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Filter toggle */}
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full
                        text-xs font-semibold transition-colors border
                        ${
                          showFilters || filtersAreActive(appliedFilters)
                            ? 'bg-primary-50 text-primary-700 border-primary-200'
                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                        }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            סינון
            {filtersAreActive(appliedFilters) && (
              <span className="w-2 h-2 rounded-full bg-primary-500" />
            )}
          </button>

          {/* Order count pill */}
          {!loading && !error && totalCount != null && totalCount > 0 && (
            <span
              className="text-xs font-semibold text-gray-500 bg-gray-100
                         px-2.5 py-1 rounded-full"
            >
              {orders.length} מתוך {totalCount}
            </span>
          )}
        </div>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <FilterBar
          filters={filters}
          onChange={setFilters}
          onClear={handleClearFilters}
          onApply={handleApplyFilters}
        />
      )}

      {/* Active filters indicator */}
      {!showFilters && filtersAreActive(appliedFilters) && (
        <div
          className="flex items-center justify-between px-4 py-2.5
                        bg-primary-50 border border-primary-200 rounded-2xl"
        >
          <span className="text-xs font-semibold text-primary-700 flex items-center gap-1.5">
            <Search className="w-3.5 h-3.5" />
            מוצגות תוצאות מסוננות
          </span>
          <button
            type="button"
            onClick={handleClearFilters}
            className="text-xs font-semibold text-primary-600 hover:text-primary-800
                       flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            נקה סינון
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
          <p className="text-sm">טוען הזמנות…</p>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex flex-col items-center gap-4 py-16">
          <div
            className="flex items-start gap-3 px-4 py-3.5 bg-red-50 border
                          border-red-200 rounded-2xl text-sm text-red-700 w-full max-w-sm"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">שגיאה בטעינת ההזמנות</p>
              <p className="text-xs mt-0.5 text-red-500">{error}</p>
            </div>
          </div>
          <button
            onClick={handleRetry}
            className="flex items-center gap-2 text-sm text-primary-600
                       hover:text-primary-700 font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            נסה שנית
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && orders.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-5">
          <div
            className="flex items-center justify-center w-20 h-20
                          rounded-3xl bg-gray-100"
          >
            <ClipboardList className="w-10 h-10 text-gray-300" />
          </div>
          <div className="text-center space-y-1.5">
            <h2 className="text-lg font-bold text-gray-800">
              {filtersAreActive(appliedFilters)
                ? 'לא נמצאו הזמנות'
                : 'אין הזמנות עדיין'}
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              {filtersAreActive(appliedFilters)
                ? 'נסה לשנות את הסינון או לנקות אותו כדי לראות את כל ההזמנות.'
                : 'ההזמנות שתשלח יופיעו כאן. תוכל לעקוב אחר הסטטוס ולראות אם הכמויות שונו על ידי המחסן.'}
            </p>
          </div>
          {filtersAreActive(appliedFilters) ? (
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-2 px-5 py-3 rounded-2xl
                         bg-primary-600 hover:bg-primary-700 active:bg-primary-800
                         text-white font-semibold text-sm transition-colors
                         active:scale-[.98]"
            >
              <X className="w-4 h-4" />
              נקה סינון
            </button>
          ) : (
            <button
              onClick={() =>
                navigate('/b2b/catalog', { state: navState })
              }
              className="flex items-center gap-2 px-5 py-3 rounded-2xl
                         bg-primary-600 hover:bg-primary-700 active:bg-primary-800
                         text-white font-semibold text-sm transition-colors
                         active:scale-[.98]"
            >
              <BookOpen className="w-4 h-4" />
              עבור לקטלוג
            </button>
          )}
        </div>
      )}

      {/* Orders list */}
      {!loading && !error && orders.length > 0 && (
        <div className="space-y-3">
          {/* Modification notice */}
          {orders.some((o) =>
            o.lines.some(
              (l) => l.approved_quantity !== l.requested_quantity,
            ),
          ) && (
            <div
              className="flex items-start gap-2.5 px-4 py-3 bg-amber-50
                            border border-amber-200 rounded-2xl text-xs text-amber-700"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                <span className="font-semibold">שים לב — </span>
                חלק מהכמויות שונו על ידי המחסן. הזמנות עם התאמות
                מסומנות ב-<strong>כמויות שונו</strong>.
              </p>
            </div>
          )}

          {orders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              isAgent={isAgent}
              onInstantApprove={handleInstantApprove}
              vatRate={vatRate}
              isVatLiable={ordersIsVatLiable}
              returnsEnabled={returnsEnabled}
              onViewDocument={handleViewDocument}
              clientId={activeClientId}
              companyId={session?.activeCompanyId ?? ''}
            />
          ))}

          {/* Load more button */}
          {hasMore && (
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="w-full flex items-center justify-center gap-2 py-3
                         rounded-2xl font-semibold text-sm border border-gray-200
                         bg-white hover:bg-gray-50 active:bg-gray-100
                         text-gray-700 transition-colors disabled:opacity-60
                         active:scale-[.99]"
            >
              {loadingMore ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ChevronsDown className="w-4 h-4" />
              )}
              {loadingMore ? 'טוען…' : 'טען הזמנות נוספות'}
            </button>
          )}
        </div>
      )}

      {/* ── Returns section ──────────────────────────────────────────── */}
      {!loading && returnsEnabled && (rmaLoading || rmaRequests.length > 0) && (
        <div className="space-y-3 mt-6">
          <button
            type="button"
            onClick={() => setRmaExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-1"
          >
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <RotateCcw className="w-4.5 h-4.5 text-orange-500" />
              בקשות החזרה
              {rmaRequests.length > 0 && (
                <span
                  className="text-xs font-semibold text-orange-600 bg-orange-100
                             px-2 py-0.5 rounded-full"
                >
                  {rmaRequests.length}
                </span>
              )}
            </h2>
            <span className="text-gray-400">
              {rmaExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </span>
          </button>

          {rmaExpanded && (
            <div className="space-y-3">
              {rmaLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-orange-400" />
                </div>
              ) : rmaRequests.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  אין בקשות החזרה
                </p>
              ) : (
                rmaRequests.map((rma) => (
                  <RMACard key={rma.id} rma={rma} />
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
